import React, { Component, useEffect } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import LegalPage from "@/pages/Legal";
import AdminApp from "@/admin/AdminApp";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { scrollToId } from "@/lib/site";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#09090B] px-6 text-center">
          <div>
            <h1 className="font-display text-2xl font-bold text-zinc-100">Da ist etwas schiefgelaufen</h1>
            <p className="mt-3 text-sm text-zinc-400">Bitte lade die Seite neu.</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-6 h-11 rounded-full bg-blue-600 px-6 text-sm font-semibold text-white"
            >
              Neu laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => scrollToId(hash.slice(1)), 120);
      return () => clearTimeout(t);
    }
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function SeoSync() {
  const c = useContent();
  const S = c.settings;
  useEffect(() => {
    const areaMatch = (S.area || "").match(/^([^,&]+)/);
    const city = (areaMatch ? areaMatch[1] : "Rotenburg (Wümme)").trim();
    document.title = `${S.company_name} – Mobile Innenraumaufbereitung in ${city}`;
    const ld = document.getElementById("ld-business");
    if (ld) {
      const phoneIntl = (S.phone_href || "").replace(/[^0-9+]/g, "");
      ld.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AutoDetailing",
        name: S.company_name,
        email: S.email,
        telephone: phoneIntl && !phoneIntl.startsWith("+") ? `+49${phoneIntl.replace(/^0/, "")}` : phoneIntl || undefined,
        address: S.address,
        areaServed: S.area || undefined,
      });
    }
  }, [S]);
  return null;
}

function PublicShell() {
  return (
    <div className="grain min-h-screen bg-[#09090B] text-zinc-100 antialiased">
      <Nav />
      <ScrollManager />
      <SeoSync />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/impressum" element={<LegalPage kind="impressum" />} />
        <Route path="/datenschutz" element={<LegalPage kind="datenschutz" />} />
        <Route path="*" element={<Home />} />
      </Routes>
      <Footer />
    </div>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ContentProvider>
          <Routes>
            <Route path="/admin/*" element={<AdminApp />} />
            <Route path="*" element={<PublicShell />} />
          </Routes>
          <Toaster position="bottom-center" />
        </ContentProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
